import Textfield from '@atlaskit/textfield';
import { useFlaggingCallback } from '@databases/flags';
import { useDebouncedModel } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { DatabaseValue } from '@databases/types/database';
import { DatabaseFilter, DatabaseFilterType } from '@databases/types/database/view';
import { ValueChange } from '@databases/utils/database/changesets';
import { getDatabaseFieldOptional } from '@databases/utils/database/utils';
import React, { FunctionComponent } from 'react';

import { useScope } from '../../../Scope';
import { InternalDatabaseValueEdit } from '../../../database-value/DatabaseValueEdit';

export interface FilterValueEditProps {
  contentId: string;
  value: DatabaseFilter;
  onChange: (value: DatabaseFilter, changes: (ValueChange | null)[]) => void;
  autoFocus?: boolean;
  appearance?: 'default' | 'none' | 'subtle' | undefined;
}

/**
 * Renders the appropriate field to edit the filter "value". The rendered field depends on the filter type. For example,
 * in case of a "contains" filter, a text field is rendered, in case of an "equals" filter, an edit field for the field
 * type of the selected database field is rendered, and in case of an "empty" field, nothing is rendered.
 */
const FilterValueEdit: FunctionComponent<FilterValueEditProps> = ({
  contentId,
  value: valueProp,
  onChange: onChangeProp,
  autoFocus = false,
  appearance,
}) => {
  const [value, onChange] = useDebouncedModel<DatabaseFilter, ValueChange | null>(
    valueProp,
    onChangeProp
  );

  const handleChange = useFlaggingCallback(
    async (val: DatabaseValue | string, change: ValueChange | null) => {
      onChange({ ...value, value: val } as any, change);
    },
    [onChange, value]
  );

  const handleInputChange = useFlaggingCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      handleChange((e.target as HTMLInputElement).value, null);
    },
    [handleChange]
  );

  const scope = useScope();
  const field = defaultStoreUtils.useSelector((state) => {
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });
    return getDatabaseFieldOptional(databaseDraft, (value as any).fieldId);
  });

  switch (value.type) {
    case DatabaseFilterType.EQUALS:
    case DatabaseFilterType.GREATER:
    case DatabaseFilterType.LESS: {
      if (field) {
        return (
          <InternalDatabaseValueEdit
            contentId={contentId}
            field={field}
            value={value.value}
            onChange={handleChange}
            autoFocus={autoFocus}
            isBulkEdit={false}
            appearance={appearance}
          />
        );
      } else {
        return null;
      }
    }

    case DatabaseFilterType.CONTAINS: {
      return (
        <Textfield
          value={value.value}
          onChange={handleInputChange}
          autoFocus={autoFocus}
          testId="filter-value-edit-contains-textfield"
        />
      );
    }

    default:
      return null;
  }
};

export default FilterValueEdit;
